/**
 * Algorithm 1: Question Score Calculation
 * Calculate individual question mastery (0-100%)
 * 
 * @param {Object} questionData - User's performance data for this question
 * @param {number} questionData.timesShown - How many times question was shown
 * @param {number} questionData.timesCorrect - How many times answered correctly
 * @param {number} questionData.hintsUsed - Total hints used
 * @param {number} questionData.lastShown - Timestamp of last showing
 * @param {number} questionData.streak - Consecutive correct answers
 * @param {number} questionDifficulty - Difficulty rating (0.0-1.0)
 * @returns {number} - Question score (0-100)
 */

import { SCORING } from '@config/constants.js';

export function calculateQuestionScore(questionData, questionDifficulty) {
  const { timesShown, timesCorrect, hintsUsed, lastShown, streak } = questionData;

  // New question - no score yet
  if (timesShown === 0) return 0;

  // Step 1: Base score from correct rate
  const correctRate = timesCorrect / timesShown;
  let score = correctRate * 100;

  // Step 2: Difficulty multiplier (harder = worth more)
  // Easy (0.2): 1.1x
  // Medium (0.5): 1.25x
  // Hard (0.8): 1.4x
  const difficultyMultiplier = 1 + questionDifficulty * SCORING.DIFFICULTY_MULTIPLIER;
  score *= difficultyMultiplier;

  // Step 3: Hint penalty
  // Each hint used reduces score by 10%, max -30%
  const hintPenalty = Math.min(hintsUsed * SCORING.HINT_PENALTY_PER_USE, SCORING.MAX_HINT_PENALTY);
  score -= hintPenalty;

  // Step 4: Consistency bonus
  // 3+ correct in a row = +15 points
  if (streak >= SCORING.CONSISTENCY_THRESHOLD) {
    score += SCORING.CONSISTENCY_BONUS;
  }

  // Step 5: Time decay
  // If not seen recently, confidence drops
  const hoursSinceLastSeen = (Date.now() - lastShown) / (1000 * 60 * 60);
  const decayPenalty = Math.min(hoursSinceLastSeen * SCORING.TIME_DECAY_RATE, SCORING.MAX_TIME_DECAY);
  score -= decayPenalty;

  // Step 6: Clamp to 0-100
  return Math.max(0, Math.min(100, score));
}
